import { Box, Button, Link as MuiLink, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { supportApi } from "../client.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { PageLoader } from "@/components/ui/page-loader";
import { EscalateModal } from "./EscalateModal.js";
import { MessageBubble } from "./MessageBubble.js";

/**
 * Conversation transcript — the full Otto exchange with the customer,
 * source citations inline, and moderation actions on the title row.
 * Flag/unflag and escalate-to-ticket are gated on `support:edit`; if
 * the user only has `support:view`, the buttons disappear server-side
 * (the proxy enforces it; we don't bother trying to render-time guess).
 */
export function ConversationDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const cid    = id ?? "0";
  const nav    = useNavigate();
  const qc     = useQueryClient();

  const conversationQuery = useQuery({
    queryKey: ["support", "conversations", "detail", cid],
    queryFn:  () => supportApi.getConversation(cid),
    enabled:  Number.isFinite(Number(cid)),
  });

  const flag = useMutation({
    mutationFn: async (reason: string) => supportApi.flagConversation(cid, reason),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["support", "conversations", "detail", cid] }),
  });
  const unflag = useMutation({
    mutationFn: async () => supportApi.unflagConversation(cid),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ["support", "conversations", "detail", cid] }),
  });

  const [showEscalate, setShowEscalate] = useState(false);

  if (conversationQuery.isLoading) return <PageLoader />;
  if (conversationQuery.isError || !conversationQuery.data) {
    return (
      <SupportErrorState
        error={conversationQuery.error}
        resourceLabel="conversation"
      />
    );
  }

  const c = conversationQuery.data.data;
  const canModerate = c.status !== "escalated";

  return (
    <Box component="section" sx={{ display: "grid", gap: 2 }}>
      <Box component="header" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.25rem", fontWeight: 600, letterSpacing: "-0.01em" }}>
            Conversation #{c.id}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
            with{" "}
            <MuiLink component={Link} to={`/support/organizations/${c.organization.id}`} color="inherit" underline="hover">
              {c.organization.name}
            </MuiLink>{" "}
            · {c.user.name}{" "}
            <Box component="span" sx={{ fontSize: 12 }}>({c.user.email})</Box>
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {canModerate && c.status !== "flagged" && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => {
                const reason = window.prompt("Flag reason (optional):", "") ?? "";
                flag.mutate(reason);
              }}
              disabled={flag.isPending}
            >
              {flag.isPending ? "Flagging…" : "Flag"}
            </Button>
          )}
          {c.status === "flagged" && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => unflag.mutate()}
              disabled={unflag.isPending}
            >
              {unflag.isPending ? "Unflagging…" : "Unflag"}
            </Button>
          )}
          {canModerate && (
            <Button variant="contained" onClick={() => setShowEscalate(true)}>
              Escalate to ticket
            </Button>
          )}
        </Box>
      </Box>

      {c.status === "escalated" && c.escalated_ticket_id && (
        <Banner tone="sky">
          Escalated into{" "}
          <MuiLink
            component={Link}
            to={`/support/tickets/${c.escalated_ticket_id}`}
            sx={{ fontWeight: 600 }}
            color="inherit"
            underline="always"
          >
            ticket #{c.escalated_ticket_id}
          </MuiLink>
          {c.escalated_by && <> by {c.escalated_by.name}</>}.
        </Banner>
      )}

      {c.status === "flagged" && (
        <Banner tone="amber">
          Flagged
          {c.flagged_by && <> by <Box component="span" sx={{ fontWeight: 600 }}>{c.flagged_by.name}</Box></>}
          {c.flag_reason && <>: {c.flag_reason}</>}.
        </Banner>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          gap: 1.5,
          borderRadius: 1,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          boxShadow: (t) => t.shadows[1],
          p: 1.5,
          fontSize: 14,
        }}
      >
        <Cell label="Persona">{c.persona}</Cell>
        <Cell label="Assistant">{c.assistant_name ?? "Otto"}</Cell>
        <Cell label="Turns">{c.turn_count}</Cell>
        <Cell label="Status">{c.status}</Cell>
        <Cell label="Started">{new Date(c.started_at).toLocaleString()}</Cell>
        <Cell label="Last activity">{new Date(c.last_activity_at).toLocaleString()}</Cell>
        <Cell label="External ID">
          <Box component="code" sx={{ fontSize: 12 }}>{c.external_conversation_id}</Box>
        </Cell>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {c.messages.map((m) => (
          <MessageBubble key={m.id} message={m} assistantName={c.assistant_name} />
        ))}
      </Box>

      {showEscalate && (
        <EscalateModal
          conversation={c}
          onCancel={() => setShowEscalate(false)}
          onConfirm={async (body) => {
            const res = await supportApi.escalateConversation(cid, body);
            setShowEscalate(false);
            nav(`/support/tickets/${res.data.ticket_id}`);
          }}
        />
      )}
    </Box>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: "amber" | "sky";
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: 1,
        border: 1,
        px: 1.5,
        py: 1,
        fontSize: 14,
        ...(tone === "amber"
          ? {
              borderColor: (t) => alpha(t.palette.warning.main, 0.4),
              bgcolor: (t) => alpha(t.palette.warning.main, 0.05),
              color: "warning.main",
            }
          : {
              borderColor: (t) => alpha(t.palette.info.main, 0.4),
              bgcolor: (t) => alpha(t.palette.info.main, 0.05),
              color: "info.main",
            }),
      }}
    >
      {children}
    </Box>
  );
}

function Cell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box>
      <Box sx={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "text.disabled" }}>
        {label}
      </Box>
      <Box sx={{ mt: 0.25 }}>{children}</Box>
    </Box>
  );
}
